/**
 * The session's navigation model, shared by the rail, the drawer, the
 * breadcrumb, the window title and the command palette (WP-18).
 *
 * Recomputed only when the role, the module list or the path changes — the
 * user object itself is re-created by unrelated store updates.
 */
import * as React from "react";
import { useLocation } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore.js";
import { userModules } from "@/config/modules.config.js";
import {
  activeIds,
  markCurrent,
  resolveActive,
  selectNavForUser,
} from "@/lib/nav-tree.js";

export interface NavNode {
  id: string;
  label: string;
  pageLabel?: string;
  shortLabel?: string;
  title?: string;
  icon?: string;
  to?: string;
  match?: string[];
  children?: NavNode[];
  module?: string;
  locked?: boolean;
  badge?: string;
  keywords?: string[];
  separatorBefore?: boolean;
  level: number;
  trail: string[];
  __current?: boolean;
}

export interface NavActive {
  leaf: NavNode;
  ancestors: NavNode[];
  pattern: string;
}

export interface NavModel {
  mode: "tree" | "flat" | "none";
  role: string | null;
  nodes: NavNode[];
  /** Resolved location (menu first, else the route-access tree, OP-F-09). */
  active: NavActive | null;
  /** Ids on the current path. */
  current: Set<string>;
}

export function useNavModel(): NavModel {
  const user = useAuthStore((s: any) => s.user);
  const { pathname } = useLocation();
  const role: string | null = user?.role ?? null;
  const modules = userModules(user);
  const modulesKey = modules ? modules.join("|") : "?";

  const menu = React.useMemo(
    () => selectNavForUser(user),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [role, modulesKey],
  );
  // A page the route admits but the menu does not list (MANAGER on /equipe)
  // still has a location: resolved on the unfiltered-by-menu tree.
  const locationTree = React.useMemo(
    () => selectNavForUser(user, { menu: false }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [role, modulesKey],
  );

  return React.useMemo(() => {
    const active = (resolveActive(menu, pathname) ?? resolveActive(locationTree, pathname)) as NavActive | null;
    const current = activeIds(active);
    return {
      mode: menu.mode as NavModel["mode"],
      role: menu.role,
      nodes: markCurrent(menu.nodes, current) as NavNode[],
      active,
      current,
    };
  }, [menu, locationTree, pathname]);
}

export default useNavModel;
